import { useState } from "react";
import {
  useDeleteCityMutation,
  useFetchCitiesQuery,
  useToggleCityIsActiveStatusMutation,
} from "../store";
import Pagination from "../components/common/Pagination";
import ToggleSwitch from "../components/common/ToggleSwitch";
import { FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import TextField from "../components/common/TextField";
import useDebounce from "../hooks/useDebounce";
import { Link } from "react-router-dom";
import { City } from "../types";
import { showToast, ToastType } from "../utils/toast.utils";
import Modal from "../components/common/Modal";
import Button from "../components/common/Button";

const Cities: React.FC = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [displayName, setDisplayName] = useState<string>("");
  const debouncedDislayName = useDebounce(displayName, 400);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [cityToDelete, setCityToDelete] = useState<City | null>(null);

  const [deleteCity] = useDeleteCityMutation();
  const [toggleActive] = useToggleCityIsActiveStatusMutation();

  const confirmDelete = (cityToDelete: City) => {
    setCityToDelete(cityToDelete);
    setShowConfirmModal(true);
  };

  const handleDeleteCity = async () => {
    if (!cityToDelete) return;
    const result = await deleteCity(cityToDelete._id);
    if (!result.error) {
      showToast("Ville supprimée", ToastType.SUCCESS);
    }
    setShowConfirmModal(false);
    setCityToDelete(null);
  };

  const { data: cities, isLoading } = useFetchCitiesQuery({
    page,
    perPage,
    displayName: debouncedDislayName,
  });

  return (
    <div className="w-full mx-auto px-6 py-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Villes</h1>
          <p className="text-gray-500 mt-1">
            Gérez la liste des villes desservies
          </p>
        </div>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="w-full md:w-64">
            <TextField
              id="search"
              value={displayName}
              onChange={(e: any) => setDisplayName(e.target.value)}
              placeholder="Rechercher une ville..."
            />
          </div>
          <Link
            to="/cities/create"
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-opacity-90 transition-opacity"
          >
            <FaPlus /> Nouvelle Ville
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4">Nom</th>
                <th className="px-6 py-4 text-center">Active</th>
                <th className="px-6 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-8 text-center">
                    Chargement...
                  </td>
                </tr>
              ) : cities?.data?.length === 0 ? (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-8 text-center text-gray-400"
                  >
                    Aucune ville trouvée.
                  </td>
                </tr>
              ) : (
                cities?.data?.map((row) => (
                  <tr
                    key={row._id}
                    className="hover:bg-gray-50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 font-medium text-gray-900">
                      {row.displayName}
                    </td>
                      <td className="px-6 py-4">
                        <div className="flex justify-center">
                          <div
                            className={`flex items-center gap-2 px-3 py-1 rounded-full border ${
                              row.isActive
                                ? "bg-green-50 border-green-200 text-green-700"
                                : "bg-gray-50 border-gray-200 text-gray-500"
                            }`}
                          >
                            <ToggleSwitch
                              isOn={row.isActive}
                              onToggle={() => {
                                toggleActive({
                                  cityId: row._id,
                                  isActive: !row.isActive,
                                });
                              }}
                            />
                            <span className="text-xs font-medium min-w-[3rem]">
                              {row.isActive ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={row._id}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                            title="Modifier"
                          >
                            <FaEdit size={14} />
                          </Link>
                          <button
                            onClick={() => confirmDelete(row)}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                            title="Supprimer"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                      </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-200 px-6 py-4">
          <Pagination
            paginationInfo={{
              page,
              perPage,
              count: cities?.paginationInfo.count || 0,
            }}
            setPage={setPage}
            setPerPage={setPerPage}
          />
        </div>
      </div>

      {showConfirmModal && (
        <Modal
          onClose={() => setShowConfirmModal(false)}
          actionBar={
            <>
              <Button
                className="bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg mr-2"
                onClick={() => setShowConfirmModal(false)}
              >
                Annuler
              </Button>
              <Button
                className="bg-red-600 text-white hover:bg-red-700 px-4 py-2 rounded-lg shadow-sm"
                onClick={handleDeleteCity}
              >
                Supprimer
              </Button>
            </>
          }
        >
          <div className="text-center p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Confirmer la suppression
            </h3>
            <p className="text-gray-500">
              Êtes-vous sûr de vouloir supprimer{" "}
              <span className="font-bold text-gray-800">
                {cityToDelete?.displayName}
              </span>{" "}
              ? Cette action est irréversible.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Cities;
